import styled from '@emotion/styled'
import { Router } from 'found'
import { TFunction } from 'i18next'
import React from 'react'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { fetchTimetable } from '../../actions/api'
import { checkInModal, openModal } from '../../actions/timetable'
import Download from '../../icons/Download'
import moment from '../../lib/moment-fi'
import { allowTimetableFetchSelector } from '../../lib/roleUtils'
import { isMobileSelector, shiftTasksToTimetableParams } from '../../Selectors'
import { color, getColor, LAYOUT_LEFT_EDGE, px, theme } from '../../Theme'
import { Action, AppState, Dispatch, Shift } from '../../types'
import { FullProps } from '../../types/App'
import { TimetableParams } from '../../types/Input'
import { TimetableModal, TimetableRequestOrigin } from '../../types/States'
import { SmallErrorText } from '../ErrorText'
import LoadingIndicator from '../LoadingIndicator'
import Text from '../Text'
import UsedForDrivingModal from './UsedForDrivingModal'

const formatPart = ({ trainNumber, depStation, arrStation, depTime, arrTime }: TimetableParams) => {
  const route = [depStation, arrStation].filter(Boolean).join('-')
  const times = [depTime, arrTime]
    .filter(Boolean)
    .map((time) => moment(time).format('HH:mm'))
    .join('-')
  return [trainNumber, route, times].filter(Boolean).join(' ')
}

interface DownloadButtonProps extends FullProps {
  buttonMode: boolean
}

const Container = styled.div<FullProps>`
  ${(p) =>
    !p.full
      ? `flex: 0 1 19%;`
      : `display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin: 0 12px 0 ${px(LAYOUT_LEFT_EDGE)};`}
`

const SpacedRow = styled.div<FullProps>`
  ${theme.layout.flexRow};
  justify-content: space-between;
  color: ${(p) => getColor(p.theme, ['black'], ['grayPink'])};
  ${(p) => p.full && `width: 100%;`}
`

const DownloadButton = styled.button<DownloadButtonProps>`
  ${(p) =>
    p.full ? theme.text('smaller', 'decorative', 'bold') : theme.text('normal', 'content', 'bold')};
  ${(p) => theme.spacing.sides(p.full ? 'large' : 'smallest')};
  ${(p) => theme.spacing.ends(p.full ? 'small' : 'tiny')};
  background: ${color('primaryBlue')};
  border: none;
  border-radius: ${theme.borderRadius.button};
  color: ${color('white')};
  cursor: pointer;
  text-transform: ${(p) => (p.full ? 'uppercase' : 'none')};
  letter-spacing: ${(p) => (p.full ? px(0.8) : 0)};
  width: ${(p) => (p.buttonMode ? `100%` : `70px`)};
  height: 34px;
  max-height: 34px;
  &[disabled] {
    color: ${(p) => getColor(p.theme, ['white'], ['grayPink'])};
    background: ${(p) => getColor(p.theme, ['shallowBlue'], ['nightGray'])};
  }
`

type Props = {
  t: TFunction
  shift: Shift
  full: boolean
  router?: Router
  fetchTimetable: (
    arg0: boolean,
    array: Array<TimetableParams>,
    requestOrigin: TimetableRequestOrigin
  ) => void
  isMobile: boolean
  timetableParams: Array<TimetableParams>
  loading: boolean
  modal: TimetableModal
  openModal: (
    isOpen: boolean,
    usedTimetableParams: Array<TimetableParams>,
    pendingRequestOrigin: TimetableRequestOrigin | null
  ) => void
  checkInModal: (arg0: boolean) => void
  allowTimetableFetch: boolean
  timetableError: string
  failedDrivingTimetableParts: TimetableParams[]
  failedNonDrivingTimetableParts: TimetableParams[]
}

function TimetableRow({
  t,
  timetableParams,
  shift,
  full,
  fetchTimetable,
  loading,
  modal,
  openModal,
  checkInModal,
  isMobile,
  allowTimetableFetch,
  timetableError,
  failedDrivingTimetableParts,
  failedNonDrivingTimetableParts,
}: Props) {
  if (!timetableParams || timetableParams.length === 0 || !allowTimetableFetch) {
    return null
  }

  return (
    <Container full={full}>
      <UsedForDrivingModal
        modal={modal}
        fetchTimetable={fetchTimetable}
        openModal={openModal}
        timetableParams={timetableParams}
        checkInModal={checkInModal}
        isMobile={isMobile}
      />
      <SpacedRow full={full}>
        <SpacedRow>
          {shift && <Text>{t('timetableForShift', { number: shift.shiftId })}</Text>}
        </SpacedRow>
        <DownloadButton
          buttonMode={!shift}
          disabled={loading}
          onClick={() =>
            openModal(true, timetableParams, {
              kind: 'shift',
              shiftId: shift.id,
            })
          }
        >
          {loading ? (
            <LoadingIndicator size="small" padded={false} />
          ) : (
            <Download iconSize="smallest" />
          )}
        </DownloadButton>
      </SpacedRow>
      {timetableError && <SmallErrorText>{t(timetableError)}</SmallErrorText>}
      {failedDrivingTimetableParts.map((part) => (
        <SmallErrorText key={`driving-${formatPart(part)}`}>
          {t('timetablePartNotFoundDriving', { part: formatPart(part) })}
        </SmallErrorText>
      ))}
      {failedNonDrivingTimetableParts.map((part) => (
        <SmallErrorText key={`nonDriving-${formatPart(part)}`}>
          {t('timetablePartNotFoundInformational', { part: formatPart(part) })}
        </SmallErrorText>
      ))}
    </Container>
  )
}

type PropsIn = {
  t: TFunction
  shift: Shift
  timetableParams?: TimetableParams
}

const mapStateToProps = (state: AppState, { shift, timetableParams }: PropsIn) => {
  const isMobile = isMobileSelector(state)

  const params = shift
    ? shiftTasksToTimetableParams(shift)
    : timetableParams
    ? [timetableParams]
    : []
  const paramsFromState = state.timetable.usedTimetableParams
  const partialFailures = state.timetable.partialFailuresByShiftId[shift.id]

  return {
    isMobile,
    timetableParams: paramsFromState && paramsFromState.length > 0 ? paramsFromState : params,
    loading: state.timetable.loading,
    timetableError:
      state.timetable.requestOrigin?.kind === 'shift' &&
      state.timetable.requestOrigin.shiftId === shift.id
        ? state.timetable.error
        : '',
    modal: state.timetable.modal,
    allowTimetableFetch: allowTimetableFetchSelector(state),
    failedDrivingTimetableParts:
      partialFailures?.driving ?? shift.failedDrivingTimetableParts ?? [],
    failedNonDrivingTimetableParts: partialFailures?.nonDriving ?? [],
  }
}
const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchTimetable: (
    isUsedForDriving: boolean,
    parts: TimetableParams[],
    requestOrigin: TimetableRequestOrigin
  ) => dispatch(fetchTimetable(isUsedForDriving, parts, requestOrigin) as unknown as Action),
  openModal: (
    isOpen: boolean,
    usedTimetableParams: TimetableParams[],
    pendingRequestOrigin: TimetableRequestOrigin | null
  ) => dispatch(openModal({ isOpen, usedTimetableParams, pendingRequestOrigin })),
  checkInModal: (checked: boolean) => dispatch(checkInModal(checked)),
})

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(TimetableRow))
